import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getUsers, createUser, updateUser, getUserRoles } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/formatDateTime';

const PERM_LABELS = {
  VIEW_ALL_APPOINTMENTS: 'Ver todos os agendamentos',
  VIEW_OWN_APPOINTMENTS: 'Ver apenas os próprios',
  CREATE_APPOINTMENT: 'Criar agendamentos',
  EDIT_APPOINTMENT: 'Editar agendamentos',
  DELETE_APPOINTMENT: 'Excluir agendamentos',
  MANAGE_USERS: 'Gerenciar usuários e perfis',
  VIEW_BILLING: 'Ver faturamento',
  MANAGE_BILLING: 'Gerenciar faturamento',
  MANAGE_TENANT_SETTINGS: 'Configurações do sistema',
  VIEW_NOTIFICATIONS: 'Ver notificações',
  MANAGE_NOTIFICATIONS: 'Gerenciar notificações',
  VIEW_AUDIT_LOGS: 'Ver logs de auditoria',
  MANAGE_INTEGRATIONS: 'Gerenciar integrações',
  CONNECT_GOOGLE_CALENDAR: 'Conectar Google Calendar',
  CONFIGURE_WHATSAPP: 'Configurar WhatsApp',
};
const permLabel = p => PERM_LABELS[p] || p.replace(/_/g, ' ');

export default function TenantUsers() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [users,     setUsers]     = useState([]);
  const [roles,     setRoles]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [toggling,  setToggling]  = useState({});
  const [form, setForm] = useState({ name:'', email:'', role_id:'', active:1 });

  useEffect(() => {
    if (!hasPermission('MANAGE_USERS')) { navigate('/app', { replace: true }); }
  }, [hasPermission, navigate]);

  const load = () => {
    setLoading(true);
    Promise.all([getUsers(), getUserRoles()])
      .then(([u, r]) => { setUsers(u.data.users); setRoles(r.data.roles); })
      .catch(() => toast.error('Erro ao carregar'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm({ name:'', email:'', role_id:'', active:1 }); setShowModal(true); };
  const openEdit   = u  => { setEditing(u); setForm({ name:u.name, email:u.email, role_id:u.role_id||'', active:u.active }); setShowModal(true); };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error('Nome e email são obrigatórios');
    if (!editing && !form.role_id) return toast.error('Selecione um perfil para o usuário');
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) { await updateUser(editing.id, payload); toast.success('Usuário atualizado!'); }
      else         { await createUser(payload);              toast.success('Usuário criado! Senha temporária enviada por e-mail.'); }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const toggleActive = async u => {
    setToggling(p => ({...p,[u.id]:true}));
    try {
      await updateUser(u.id, { active: u.active ? 0 : 1 });
      toast.success(`Usuário ${u.active ? 'desativado' : 'ativado'}!`);
      load();
    } catch { toast.error('Erro'); }
    finally { setToggling(p => ({...p,[u.id]:false})); }
  };

  const selectedRole = roles.find(r => r.id === parseInt(form.role_id));

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header"><h2>Usuários</h2><p>{users.length} usuário(s)</p></div>
        <button className="btn btn-primary" onClick={openCreate}>+ Novo Usuário</button>
      </div>

      {loading ? <div className="spinner" style={{margin:'40px auto',display:'block'}} /> : (
        users.length === 0
          ? <div className="empty-state"><div className="empty-icon">👥</div><h3>Nenhum usuário</h3></div>
          : <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {users.map(u => {
                const role = roles.find(r => r.id === u.role_id);
                return (
                  <div key={u.id} className="mobile-card">
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <div className="user-avatar" style={{ width:40, height:40, fontSize:16, background:'var(--primary)', flexShrink:0 }}>{u.name[0]}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                          <strong style={{ fontSize:15 }}>{u.name}</strong>
                          <span className={`badge ${u.active ? 'badge-success' : 'badge-danger'}`}>{u.active ? 'Ativo' : 'Inativo'}</span>
                        </div>
                        <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>{u.email}</div>
                        <div style={{ marginTop:6, display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                          {role && <span className="badge badge-info">{role.name}</span>}
                          {u.last_login_at
                            ? <span style={{ fontSize:11, color:'var(--gray-400)' }}>Login: {formatDate(u.last_login_at)}</span>
                            : <span style={{ fontSize:11, color:'var(--gray-400)' }}>Nunca logou</span>
                          }
                        </div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8, marginTop:12 }}>
                      <button className="btn btn-secondary btn-sm" style={{ flex:1 }} onClick={() => openEdit(u)}>Editar</button>
                      {/* Não mostra botão de ativar/desativar para o próprio usuário logado */}
                      {String(u.id) !== String(user?.id) && (
                        <button
                          className={`btn btn-sm ${u.active ? 'btn-warning' : 'btn-success'}${toggling[u.id] ? ' btn-loading' : ''}`}
                          style={{ flex:1 }} disabled={toggling[u.id]} onClick={() => toggleActive(u)}>
                          {toggling[u.id] ? '' : u.active ? 'Desativar' : 'Ativar'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editing ? 'Editar Usuário' : 'Novo Usuário'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Nome *</label>
                    <input value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Perfil *</label>
                  <select value={form.role_id} onChange={e => set('role_id', e.target.value)} required>
                    <option value="">Selecione um perfil</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                {!editing && (
                  <div className="info-box" style={{ fontSize:12 }}>
                    Uma senha temporária será gerada automaticamente e enviada para o e-mail cadastrado. O usuário deverá redefinir no primeiro acesso.
                  </div>
                )}
                {editing && (
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.active} onChange={e => set('active', parseInt(e.target.value))}>
                      <option value={1}>Ativo</option>
                      <option value={0}>Inativo</option>
                    </select>
                  </div>
                )}
                {selectedRole?.permissions?.length > 0 && (
                  <div>
                    <div style={{fontSize:12,fontWeight:600,marginBottom:6,color:'var(--gray-600)'}}>Permissões do perfil</div>
                    <div className="features-list">
                      {selectedRole.permissions.map(p => <span key={p} className="feature-tag" style={{fontSize:10}}>{permLabel(p)}</span>)}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving?' btn-loading':''}`} disabled={saving}>
                  {saving ? 'Salvando...' : editing ? 'Salvar' : 'Criar Usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
